import React, {
    useState,
    useEffect,
    useRef,
    useCallback,
    useMemo
} from "react";

import axios from "../../api/axios";
import { DateTime } from "luxon";
import {
    useLocation,
    useNavigate,
    useParams
} from "react-router-dom";
import { useSnackbar } from "notistack";
import { useUser } from "../../Hooks/useUser";

import TeamDatesSelector from "./Teams/TeamDatesSelector";
import TeamCreate from "./Teams/TeamCreate";
import TeamJoin from "./Teams/TeamJoin";
import AttendeeForm from "./AttendeeForm";
import ProfileSections from "./ProfileSections";
import ProfileForm from "./SaveComponentButton";

//added to queue the tasks to prisma can handle multiple connections
async function runInBatches(tasks, batchSize = 10) {
    for (let i = 0; i < tasks.length; i += batchSize) {
        await Promise.all(
            tasks.slice(i, i + batchSize).map(task => task())
        );
    }
}

// A group/team only counts if it actually has a name. Endpoints that return
// `{}` or `[]` when nothing matches are truthy but are NOT a usable group.
const isValidGroup = (group) =>
    !!group &&
    !Array.isArray(group) &&
    typeof group === "object" &&
    !!group.BPGroupName;


const Register = (props) => {
    const { enqueueSnackbar } = useSnackbar();

    const params = useParams();
    const eventid = params.eventid;

    const location = useLocation();
    const navigate = useNavigate();

    const details = location.state;
    const oldUserData = useUser();

    const insuranceId = details?.Insurance;

    const [insuranceOption, setInsuranceOption] = useState(null);

    const [User, setUser] = useState(null);
    
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [submissionFailed, setSubmissionFailed] = useState(false);

    // Prevents multiple clicks before React has time to re-render.
    const submissionLockRef = useRef(false);

    const [formsReady, setFormsReady] = useState({});
    //const [isAddition, setIsAddition] = useState(false);

    const [team, setTeam] = useState(null);


    // ─────────────────────────────────────────────────────────────────────
    // Form ready state
    // ─────────────────────────────────────────────────────────────────────

    const handleFormReadyChange = useCallback(
        (globalIndex, ready) => {
            setFormsReady(prev => ({
                ...prev,
                [globalIndex]: ready
            }));
        },
        []
    );


    // ─────────────────────────────────────────────────────────────────────
    // Date helpers
    // ─────────────────────────────────────────────────────────────────────

    const toLuxon = (val) => {
        if (!val) return null;

        if (DateTime.isDateTime(val)) {
            return val;
        }

        if (val instanceof Date) {
            return DateTime.fromJSDate(val);
        }

        if (typeof val === "string") {
            return DateTime.fromISO(val);
        }

        return null;
    };


    const toISOSafe = (val) => {
        if (!val) return null;

        if (DateTime.isDateTime(val)) {
            return val.toISODate();
        }

        if (val instanceof Date) {
            return DateTime.fromJSDate(val).toISODate();
        }

        if (typeof val === "string") {
            return val.slice(0, 10);
        }

        return null;
    };


    // ─────────────────────────────────────────────────────────────────────
    // Normalize registrations
    //
    // NOTE:
    // Registration mapping intentionally left unchanged.
    // ─────────────────────────────────────────────────────────────────────

    const normalizedRegistrations = (() => {
        if (
            details.Registrations &&
            Array.isArray(details.Registrations)
        ) {
            return details.Registrations
                .filter(
                    r => parseInt(r.Quantity) > 0
                )
                .map(r => ({
                    Registration: r,
                    Quantity: parseInt(r.Quantity),
                }));
        }

        if (details.Registration) {
            const qty = parseInt(details.Quantity);

            return [
                {
                    Registration: details.Registration,
                    Quantity: qty > 0 ? qty : 1
                }
            ];
        }

        return [];
    })();


    // ─────────────────────────────────────────────────────────────────────
    // Team requirement
    // ─────────────────────────────────────────────────────────────────────

    const eventType = details?.Event?.BP_Type || "";

const isProject =
    eventType.toLowerCase() === "project";

const isDisasterRelief =
    eventType.toLowerCase() === "disaster relief";

const isProgram = eventType.toLowerCase() === "program";


const needsTeam =
    !!(
        details.Event?.BP_Reserved ||
        normalizedRegistrations.some(
            ({ Registration }) =>
                isProject &&
                Registration.AllowTeams
        )
    );


const needsGroup =
    normalizedRegistrations.some(
        ({ Registration }) =>
            !isProject &&
            !isDisasterRelief &&
            Registration.AllowGroups
    );


const teamConfirmed = isValidGroup(team);


    // ─────────────────────────────────────────────────────────────────────
    // Dates
    // ─────────────────────────────────────────────────────────────────────

    const [attendeeDates, setAttendeeDates] = useState({
        departureDate: "",
        arrivalDate: "",
    });


    // ─────────────────────────────────────────────────────────────────────
    // Form refs
    // ─────────────────────────────────────────────────────────────────────

    const formRefs = useRef([]);
    const formRefMap = [];


    normalizedRegistrations.forEach(
        ({ Registration, Quantity }) => {

            const regType =
                parseInt(Registration.RegistrationType);

            if (regType === 1) {

                for (let i = 0; i < Quantity; i++) {
                    formRefMap.push({
                        Registration,
                        Quantity,
                        index: i,
                        type: 1
                    });
                }

            } else if (regType === 0) {

                formRefMap.push({
                    Registration,
                    Quantity,
                    index: 0,
                    type: 0
                });

            }

            // RegistrationType === 2
            // intentionally does NOT create a form ref.
        }
    );


    if (
        formRefs.current.length !== formRefMap.length
    ) {
        formRefs.current =
            formRefMap.map(
                (_, i) =>
                    formRefs.current[i] ||
                    React.createRef()
            );
    }


    // ─────────────────────────────────────────────────────────────────────
    // Effects
    // ─────────────────────────────────────────────────────────────────────

   
useEffect(() => {
    const hookUser = oldUserData?.user;
    const userId = hookUser?.UserID;

    // No user ID yet: wait for useUser() to finish loading.
    if (!userId) {
        return;
    }

    let cancelled = false;

    // Use the hook's user immediately as a fallback.
    setUser(hookUser);

    const fetchUser = async () => {
        try {
            const res = await axios.get(`/api/users/${userId}`);

            // Support either { user: {...} } or a direct user object.
            const fetchedUser = res.data?.user ?? res.data;

            if (!fetchedUser?.UserID) {
                throw new Error("The user API returned an invalid user.");
            }

            if (!cancelled) {
                setUser(fetchedUser);
            }
        } catch (error) {
            console.error(
                "Failed to fetch the complete user; using hook data:",
                error
            );

            // Keep the hook user as the fallback.
        }
    };

    fetchUser();

    return () => {
        cancelled = true;
    };
}, [oldUserData?.user?.UserID]);



    useEffect(() => {

        if (User) {
            CheckTeamExists();
        }

    }, [User]);


    useEffect(() => {

        if (!insuranceId || insuranceId === "0") {
            return;
        }

        const fetchInsurance = async () => {

            try {

                const response =
                    await axios.get(
                        `/api/insurance/${insuranceId}`
                    );

                setInsuranceOption(
                    response.data
                );

            } catch (e) {

                console.error(
                    "Failed to fetch insurance option",
                    e
                );

            }
        };

        fetchInsurance();

    }, [insuranceId]);


    /*
 * Look for an existing order this user already placed for this event.
 *
 * The Order table has no EventID, so we:
 *   1. fetch order items by EventID
 *   2. collect the distinct OrderIDs
 *   3. load those orders and keep only ones owned by the current user
 *
 * Step 3 matters: order items by EventID include every attendee's
 * orders, not just the current user's.
 *
 * Returns { order, items } or null. Fails soft (returns null), so a lookup
 * error falls back to creating a new order like before.
 */

async function hasExistingOrder(eventId, userId) {
    const response = await axios.get(
        `/api/orders/${eventId}/user/${userId}`
    );

    return response.data;
}


/*
 * Does this user already own a group for this event? Used so a second
 * registration (adding more attendees later) joins the same group
 * instead of spinning up a duplicate.
 *
 * Only a group with a BPGroupName counts. An empty object/array from the
 * endpoint is treated as "no group" so a new one gets created.
 */
async function findExistingGroup(eventId, userId) {
    try {
        const res = await axios.get(
            `/api/groups/checkgroup/${eventId}/${userId}`
        );

        return isValidGroup(res.data) ? res.data : null;
    } catch (e) {
        console.warn("Existing group lookup failed:", e);
        return null;
    }
}

   async function createAutomaticGroup() {
    try {
        const currentUserId = oldUserData?.user?.UserID;

        if (!currentUserId) {
            throw new Error(
                "Unable to determine the current user's ID."
            );
        }

        const groupRegistration =
            normalizedRegistrations.find(
                ({ Registration }) =>
                    !isProject &&
                    !isDisasterRelief &&
                    Registration.AllowGroups
            )?.Registration;

        if (!groupRegistration) {
            return null;
        }

        /*
         * Reuse the user's existing group for this event, if one
         * already exists, instead of creating a duplicate.
         */
        const existingGroup = await findExistingGroup(
            eventid,
            currentUserId
        );

        if (existingGroup) {
            return existingGroup;
        }

        const timestamp = Date.now();

        const groupName =
            `${oldUserData?.user?.FirstName || "User"} ${
                oldUserData?.user?.LastName || ""
            } Group ${timestamp}`;

        const payload = {
            BPEventID: Number(eventid),
            BPGroupName: groupName,
            BPGroupOwner: Number(currentUserId),
            BPGroupSize: 0
        };

        const response = await axios.post(
            "/api/groups/group",
            payload
        );

        const createdGroup = response.data;

        if (!isValidGroup(createdGroup)) {
            console.error(
                "Group creation returned an unusable group:",
                createdGroup
            );

            throw new Error(
                "Group creation returned no group name."
            );
        }

        return createdGroup;

    } catch (error) {

        console.error(
            "Failed to automatically create group:",
            error
        );

        enqueueSnackbar(
            error?.response?.data?.error ||
            error?.message ||
            "Unable to create your group.",
            {
                variant: "error"
            }
        );

        throw error;
    }
}


    function CheckTeamExists() {

        axios
            .get(`/api/groups/getgroup/${eventid}`)
            .then(res => {

                // Only accept a real group. `{}` / `[]` are truthy but would
                // block automatic group creation and leave the attendee
                // with no BPAttendeeGroup.
                if (isValidGroup(res.data)) {
                    setTeam(res.data);
                }

            })
            .catch(() => {
            });
    }


    // ─────────────────────────────────────────────────────────────────────
    // Team form
    // ─────────────────────────────────────────────────────────────────────

   function serveTeamForm() {

    /*
     * Only Project teams or reserved events use
     * the TeamCreate / TeamJoin components.
     *
     * AllowGroups does NOT come through here.
     */

    if (!needsTeam) {
        return null;
    }


    if (teamConfirmed) {

        return (
            <div className="alert alert-success mt-4 d-flex flex-wrap align-items-center justify-content-between">

                <span>
                    <strong>Team:</strong>{" "}
                    {team.BPGroupName}
                    {" "}
                    &mdash; you're all set!
                </span>

                <a
                    className="button-default button-gray ms-2"
                    onClick={() => setTeam(null)}
                    role="button"
                    style={{
                        minWidth: "120px",
                        padding: "8px 16px",
                        cursor: "pointer"
                    }}
                >
                    Change Team
                </a>

            </div>
        );
    }


    /*
     * Team Leaders create a new team (and set its name + password).
     * Team Members join an existing team (enter the team name + password).
     * Leader vs member is identified by the registration's SectionLabel,
     * matching the isTeamLeader convention used elsewhere.
     */
    const registeringAsLeader = normalizedRegistrations.some(
        ({ Registration }) =>
            isProject &&
            Registration.AllowTeams === true &&
            Registration.SectionLabel === "Leader"
    );


    if (details.Event?.BP_Reserved || !registeringAsLeader) {

        return (
            <TeamJoin
                onTeamReady={setTeam}
            />
        );
    }


    /*
     * Team Leader registrations create the team.
     */
    return (
        <TeamCreate
            onTeamReady={setTeam}
        />
    );
}


    // ─────────────────────────────────────────────────────────────────────
    // Arrival dates
    // ─────────────────────────────────────────────────────────────────────

    function serveArrivalDates() {

        if (details.Event?.BP_DatesFlexible) {

            return (
                <TeamDatesSelector
                    onDatesChange={
                        dates =>
                            setAttendeeDates(dates)
                    }
                    Event={details.Event}
                />
            );
        }

        return null;
    }


    // ─────────────────────────────────────────────────────────────────────
    // Insurance
    // ─────────────────────────────────────────────────────────────────────

    const extractPrice = (priceString) => {

        if (!priceString) {
            return 0;
        }

        const match =
            String(priceString).match(/[\d.]+/);

        return match
            ? parseFloat(match[0])
            : 0;
    };


    const insuranceCost = useMemo(() => {

        if (
            !insuranceOption ||
            !insuranceId ||
            insuranceId === "0"
        ) {
            return 0;
        }


        let departure;
        let arrival;


        if (
            details.Event?.BP_DatesFlexible &&
            attendeeDates.departureDate &&
            attendeeDates.arrivalDate
        ) {

            departure =
                toLuxon(
                    attendeeDates.departureDate
                );

            arrival =
                toLuxon(
                    attendeeDates.arrivalDate
                );


            if (
                !departure?.isValid ||
                !arrival?.isValid
            ) {
                return 0;
            }

        } else {

            if (
                !details.Event?.BP_StartDate ||
                !details.Event?.BP_EndDate
            ) {
                return 0;
            }

            departure = DateTime.fromISO(details.Event.BP_EndDate);
            arrival   = DateTime.fromISO(details.Event.BP_StartDate);

            // departure =
            //     DateTime.fromISO(
            //         details.Event.BP_StartDate
            //     );

            // arrival =
            //     DateTime.fromISO(
            //         details.Event.BP_EndDate
            //     );
        }


        const days =
            departure.diff(
                arrival,
                "days"
            ).days + 1;


        if (days <= 0) {
            return 0;
        }


        const dailyRate =
            extractPrice(
                insuranceOption.price
            );


        return dailyRate * days;

    }, [
        insuranceOption,
        insuranceId,
        attendeeDates,
        details.Event
    ]);


    // ─────────────────────────────────────────────────────────────────────
    // Order item helpers
    // ─────────────────────────────────────────────────────────────────────

    const createInsuranceOrderItem = async (
        createdOrder,
        attendee
    ) => {

        const res =
            await axios.post(
                "/api/orders/order-items",
                {
                    OrderID:
                        Number(createdOrder.OrderID),

                    Quantity: 1,

                    UnitPrice:
                        insuranceCost,

                    TotalPrice:
                        insuranceCost,

                    EventID:
                        Number(eventid),

                    AttendeeID:
                        Number(
                            attendee.BPAttendeeID
                        ),

                    Description:
                        `Insurance: ${insuranceOption.label}`,
                }
            );

        return res.data;
    };


    const createTeamCostOrderItem = async (
        createdOrder,
        attendee,
        registration
    ) => {

        const requiredPrice =
            parseFloat(
                registration.RegistrationRequiredPrice ||
                0
            );


        const res =
            await axios.post(
                "/api/orders/order-items",
                {
                    OrderID:
                        Number(createdOrder.OrderID),

                    Quantity: 1,

                    UnitPrice:
                        requiredPrice,

                    TotalPrice:
                        requiredPrice,

                    EventID:
                        Number(eventid),

                    AttendeeID:
                        Number(
                            attendee.BPAttendeeID
                        ),

                    Description:
                        registration.RegistrationRequiredCostName
                            ? `Team Required Cost: ${registration.RegistrationRequiredCostName}`
                            : "Team Required Cost",
                }
            );

        return res.data;
    };


    // ─────────────────────────────────────────────────────────────────────
    // NONE / HEAD COUNT ATTENDEE CREATION
    //
    // RegistrationType === 2
    //
    // These attendees:
    // - Have no form
    // - Belong to the currently logged-in user
    // - Use SectionLabel as First Name
    // - Use 1, 2, 3... as Last Name
    // ─────────────────────────────────────────────────────────────────────

    const createHeadCountAttendees = async (
        registration,
        quantity,
        groupOverride
    ) => {

        const attendees = [];

        const currentUserId =
            oldUserData?.user?.UserID;


        if (!currentUserId) {

            throw new Error(
                "Unable to determine the current user's ID."
            );
        }


        const sectionLabel =
            registration.SectionLabel ||
            registration.RegistrationName ||
            "Attendee";


        for (
            let i = 0;
            i < quantity;
            i++
        ) {

            const payload = {

                BPEventID:
                    Number(
                        details.Event.EventsID
                    ),

                // Always attach the head-count
                // attendee to the logged-in user.
                BPAttendeeUserID:
                    Number(currentUserId),


                // Head-count identity.
                BPAttendeeFN:
                    sectionLabel,

                BPAttendeeLN:
                    String(i + 1),


                BPAttendeeType:
                    registration.RegistrationName,

                BPAttendeeLabel:
                    sectionLabel,

                BPRegistrationID:
                    Number(
                        registration.RegistrationID
                    ),


                BPAttendeeBalance:
                    parseFloat(
                        registration.RegistrationCost ||
                        0
                    ) + insuranceCost,


                BPAttendeeStatus:
                    registration.RequireApproval
                        ? "Pending"
                        : "Approved",


                BPAttendeeApproved:
                    !registration.RequireApproval,


                // Prefer the group just created/resolved for THIS submission;
                // state `team` is stale during the same submit cycle.
                ...((groupOverride ?? team) && {
                    BPAttendeeGroup:
                        (groupOverride ?? team).BPGroupName
                }),


                ...(details.Event.BP_DatesFlexible && {
                    BPAttendeeArrivalDate:
                        toISOSafe(
                            attendeeDates.arrivalDate
                        ),

                    BPAttendeeDepartureDate:
                        toISOSafe(
                            attendeeDates.departureDate
                        ),
                }),
            };


            try {

                const response =
                    await axios.post(
                        "/api/attendees",
                        payload
                    );


                const attendee =
                    response.data;


                if (
                    !attendee?.BPAttendeeID
                ) {

                    throw new Error(
                        `No BPAttendeeID returned for ${sectionLabel} ${i + 1}`
                    );
                }


                attendees.push({

                    attendee,

                    registration,

                    insuranceCost,

                    isHeadCount: true,

                    headCountIndex: i
                });


            } catch (error) {

                console.error(
                    "Failed to create head-count attendee:",
                    error
                );


                if (
                    error?.response?.status === 409
                ) {

                    enqueueSnackbar(
                        `${sectionLabel} ${i + 1} already exists for this user.`,
                        {
                            variant: "warning"
                        }
                    );

                } else {

                    enqueueSnackbar(
                        error?.response?.data?.error ||
                        `Failed to create ${sectionLabel} ${i + 1}.`,
                        {
                            variant: "error"
                        }
                    );
                }


                throw error;
            }
        }


        return attendees;
    };


    // ─────────────────────────────────────────────────────────────────────
    // Submit
    // ─────────────────────────────────────────────────────────────────────

    async function handleSubmitAll() {

      // Synchronous guard against double-clicks and repeated submissions.
    if (submissionLockRef.current) {
        return;
    }

    submissionLockRef.current = true;
    setIsSubmitting(true);
    setSubmissionFailed(false);

    const results = [];
    let attendeeCreationStarted = false;

    try {

         const existing = await hasExistingOrder(
            eventid,
            oldUserData.user.UserID
        );

        // Only a group with a real name counts as "already have one".
        let activeGroup = isValidGroup(team) ? team : null;

        if (needsGroup && !activeGroup) {
            activeGroup = await createAutomaticGroup();

            if (!isValidGroup(activeGroup)) {
                // No attendees have been submitted yet.
                submissionLockRef.current = false;
                setIsSubmitting(false);
                return;
            }

            setTeam(activeGroup);
        }

        // From this point onward, a failure may occur after one or more
        // attendees have already been saved.
        attendeeCreationStarted = true;

            // =============================================================
            // 1. Submit all actual forms
            //
            // Type 0 = Profile
            // Type 1 = Attendee Form
            //
            // Type 2 does not have a form/ref.
            //
            // activeGroup is passed to every form's submit() so the group
            // created above is written onto each attendee record.
            // =============================================================

            for (
                let i = 0;
                i < formRefs.current.length;
                i++
            ) {

                const ref =
                    formRefs.current[i];


                if (
                    !ref?.current?.submit
                ) {
                    continue;
                }


                const result =
                    await ref.current.submit(activeGroup);


                if (!result) {
                     // The form was not successfully submitted. Allow the user to correct it.
                    submissionLockRef.current = false;
                    setIsSubmitting(false);
                    return;
                }


                results.push(result);
            }


            // =============================================================
            // 2. Create Type 2 / NONE head-count attendees
            // =============================================================

            for (
                const {
                    Registration,
                    Quantity
                }
                of normalizedRegistrations
            ) {

                const regType =
                    parseInt(
                        Registration.RegistrationType
                    );


                // RegistrationType "2" = None
                if (regType !== 2) {
                    continue;
                }


                const quantity =
                    parseInt(Quantity) || 0;


                if (quantity <= 0) {
                    continue;
                }


                const headCountResults =
                    await createHeadCountAttendees(
                        Registration,
                        quantity,
                        activeGroup
                    );


                results.push(
                    ...headCountResults
                );
            }


            // =============================================================
            // 3. Make sure something was created
            // =============================================================

            if (results.length === 0) {

                enqueueSnackbar(
                    "No attendees were created.",
                    {
                        variant: "warning"
                    }
                );

                submissionLockRef.current = false;
                setIsSubmitting(false);

                return;
            }


            //console.log(
            //    "All attendee results:",
            //    results
            //);


            // =============================================================
            // 4. Build attendee summary
            // =============================================================

            const attendees =
                results.map(r => ({
                    BPAttendeeType:
                        r.attendee.BPAttendeeType,

                    BPAttendeeFN:
                        r.attendee.BPAttendeeFN,

                    BPAttendeeLN:
                        r.attendee.BPAttendeeLN,

                    BPAttendeeEmail:
                        r.attendee.BPAttendeeEmail,
                }));


            // =============================================================
// 5. Calculate order totals
//
// Insurance = ONE charge for the entire order
//
// Registration cost = PER ATTENDEE
//
// Required/team cost = PER ATTENDEE
// =============================================================

const toNumber = (value) => {

    if (
        value === null ||
        value === undefined ||
        value === ""
    ) {
        return 0;
    }

    const parsed =
        typeof value === "number"
            ? value
            : parseFloat(
                String(value).replace(
                    /[^0-9.-]/g,
                    ""
                )
            );

    return Number.isFinite(parsed)
        ? parsed
        : 0;
};


// -------------------------------------------------------------
// Per-attendee costs
// -------------------------------------------------------------

const attendeeCosts = results.map((result) => {

    const registration =
        result.registration || {};


    const registrationCost =
        toNumber(
            registration.RegistrationCost
        );


    const requiredCost =
        registration.RegistrationRequiredCost
            ? toNumber(
                registration.RegistrationRequiredPrice
            )
            : 0;


    return {
        result,

        registrationCost,

        requiredCost,

        attendeeTotal:
            registrationCost +
            requiredCost
    };

});


// -------------------------------------------------------------
// Registration costs
// -------------------------------------------------------------

const registrationCostTotal =
    attendeeCosts.reduce(
        (sum, item) =>
            sum + item.registrationCost,
        0
    );


// -------------------------------------------------------------
// Required/team costs
//
// These are PER ATTENDEE.
// -------------------------------------------------------------

const totalTeamCost =
    attendeeCosts.reduce(
        (sum, item) =>
            sum + item.requiredCost,
        0
    );


// -------------------------------------------------------------
// Insurance
//
// IMPORTANT:
// Insurance is ONE charge for the order.
// Do NOT multiply by results.length.
// -------------------------------------------------------------

// -------------------------------------------------------------
// Existing order lookup
// -------------------------------------------------------------

const existingOrder = existing?.order || null;
const isAdditionNow = !!existingOrder;

// Insurance is one charge per order. If this order already has an
// insurance item for the event, don't charge it a second time.
const insuranceAlreadyCharged = !!existing?.items?.some(item =>
    String(item.Description || "").startsWith("Insurance:")
);

const totalInsuranceCost = insuranceAlreadyCharged
    ? 0
    : toNumber(insuranceCost);


// -------------------------------------------------------------
// Final order total
// -------------------------------------------------------------

const totalBalance =
    registrationCostTotal +
    totalTeamCost +
    totalInsuranceCost;


//console.log("=================================");
//console.log("ORDER COST BREAKDOWN");
//console.log("=================================");

console.table(
    attendeeCosts.map((item) => ({
        registration:
            item.result.registration?.RegistrationName,

        section:
            item.result.registration?.SectionLabel,

        registrationCost:
            item.registrationCost,

        requiredCost:
            item.requiredCost,

        attendeeTotal:
            item.attendeeTotal
    }))
);


// =============================================================
// 6. Reuse the user's existing order for this event, or create one
// =============================================================

const notesParts = [
    `Registration for event ${eventid}`
];

if (totalInsuranceCost > 0) {
    notesParts.push(`insurance $${totalInsuranceCost.toFixed(2)}`);
}

if (totalTeamCost > 0) {
    notesParts.push(`required cost $${totalTeamCost.toFixed(2)}`);
}

const newProducts = results
    .map(r => `${r.registration.RegistrationName} - ${r.attendee.BPAttendeeID}`)
    .join(", ");

let order = null;

try {

    if (existingOrder) {

        const updatedFields = {
            Balance:
                toNumber(existingOrder.Balance) + totalBalance,

            Products:
                [existingOrder.Products, newProducts]
                    .filter(Boolean)
                    .join(", "),

            Notes:
                [existingOrder.Notes, notesParts.join(" + ")]
                    .filter(Boolean)
                    .join(" | "),

            updatedAt: DateTime.now().toString(),
        };

        await axios.put(
            `/api/orders/${existingOrder.OrderID}`,   // <-- confirm endpoint
            updatedFields
        );

        order = { ...existingOrder, ...updatedFields };

    } else {

        const orderRes = await axios.post("/api/orders", {
            OrderDate: DateTime.now().toString(),
            Products: newProducts,
            PaidToDate: 0,
            Balance: totalBalance,
            UserID: oldUserData.user.UserID,
            Notes: notesParts.join(" + "),
            createdAt: DateTime.now().toString(),
            updatedAt: DateTime.now().toString(),
        });

        order = orderRes.data;
    }


} catch (e) {
    console.error("Failed to create/update order:", e);

    setSubmissionFailed(true);

    enqueueSnackbar(
        "Order creation failed. Your attendees may already have been saved. " +
        "Please do not submit again. Refreshing the page could create duplicates. " +
        (e.response?.data?.error || e.message),
        { variant: "error" }
    );

    // Intentionally keep the submission lock in place.
    // Attendees may already exist, so another attempt is unsafe.
    return;
}



            // =============================================================
// 7. Create order items
//
// Registration cost:
//     ONE item per attendee
//
// Required/team cost:
//     ONE item per attendee
//
// Insurance:
//     ONE item for the entire order
// =============================================================

try {

    const orderItemPromises = [];


    // =========================================================
    // Registration + required cost PER ATTENDEE
    // =========================================================

    results.forEach((result) => {

        const registration =
            result.registration || {};


        const registrationCost =
            toNumber(
                registration.RegistrationCost
            );


        const requiredCost =
            registration.RegistrationRequiredCost
                ? toNumber(
                    registration.RegistrationRequiredPrice
                )
                : 0;


        // -----------------------------------------------------
        // Registration cost
        // -----------------------------------------------------

        //allow for orderitem to be created even when price is 0, so that we can track the attendee and registration in the order items table
        //if (registrationCost > 0) {

            orderItemPromises.push(

                axios.post(
                    "/api/orders/order-items",
                    {
                        OrderID:
                            order.OrderID,

                        Quantity: 1,

                        UnitPrice:
                            registrationCost,

                        TotalPrice:
                            registrationCost,

                        EventID:
                            details.Event.EventsID,

                        AttendeeID:
                            result.attendee.BPAttendeeID,

                        Description:
                            `${details.Event.BP_Title} — ${registration.RegistrationName}`,
                    }
                )

            );

        //}


        // -----------------------------------------------------
        // Required / Team cost
        // -----------------------------------------------------

        if (requiredCost > 0) {

            orderItemPromises.push(

                createTeamCostOrderItem(
                    order,
                    result.attendee,
                    registration
                )

            );

        }

    });


    // =========================================================
    // Insurance
    //
    // ONE TIME per order.
    // =========================================================
    const insuranceAttendeeId =
    results[0]?.attendee?.BPAttendeeID;

    if (
        insuranceId &&
        insuranceId !== "0" &&
        totalInsuranceCost > 0
    ) {

        /*
         * Attach the insurance charge to the first attendee
         * only so there is still an AttendeeID available for
         * the order-item record.
         */
        orderItemPromises.push(

            axios.post(
                "/api/orders/order-items",
                {
                    OrderID:
                        order.OrderID,

                    Quantity: 1,

                    UnitPrice:
                        totalInsuranceCost,

                    TotalPrice:
                        totalInsuranceCost,

                    EventID:
                        details.Event.EventsID,

                    AttendeeID:
                        insuranceAttendeeId,

                    Description:
                        `Insurance: ${insuranceOption?.label || "Supplemental Insurance"}`
                }
            )

        );

    }


    await Promise.all(
        orderItemPromises
    );


} catch (e) {
    console.error("Failed to create order items:", e);

    setSubmissionFailed(true);

    enqueueSnackbar(
        "Order items could not be saved. Your attendees or order may already exist. " +
        "Please do not submit again until the registration has been checked. " +
        (e.response?.data?.error || e.message),
        { variant: "error" }
    );

    // Intentionally keep the submission lock in place.
    return;
}


            // =============================================================
            // 8. Navigate
            // =============================================================

            setIsSubmitting(false);

            const primaryResult = results[0];


            const hasCost =
                totalBalance > 0;


            const requiresApproval =
                results.some(
                    r =>
                        r.registration
                            .RequireApproval
                );


            const requiresDeposit =
                results.some(
                    r =>
                        r.registration
                            .RegistrationAllowDeposit
                );


            if (
                hasCost &&
                (
                    !requiresApproval ||
                    requiresDeposit
                )
            ) {

                navigate(
                    `/makepayment/order/${order.OrderID}`,
                    {
                        state: {
                            fromRegistration: true,

                            event:
                                details.Event,

                            attendee:
                                primaryResult.attendee,

                            registration:
                                primaryResult.registration,

                            order,

                            allResults:
                                results,

                            attendees,
                            isAddition: isAdditionNow
                        }
                    }
                );

            } else {

                navigate(
                    "/events/thanks",
                    {
                        state: {
                            event:
                                details.Event,

                            attendee:
                                primaryResult.attendee,

                            registration:
                                primaryResult.registration,

                            order,

                            allResults:
                                results,

                            attendees,
                            isAddition: isAdditionNow
                        }
                    }
                );
            }


        } catch (error) {

            console.error(
                "Registration submission failed:",
                error
            );


            enqueueSnackbar(
                error?.response?.data?.error ||
                error?.message ||
                "Registration failed.",
                {
                    variant: "error"
                }
            );


           if (attendeeCreationStarted) {
                // Keep locked because some attendees may have been saved.
                setSubmissionFailed(true);
            } else {
                // No attendee creation started, so a retry is safe.
                submissionLockRef.current = false;
                setIsSubmitting(false);
            }
        }
    }


    // ─────────────────────────────────────────────────────────────────────
    // Form readiness
    // ─────────────────────────────────────────────────────────────────────

    const allFormsReady =
        formRefMap.length === 0
            ? true
            : formRefMap.every(
                (_, i) =>
                    formsReady[i] === true
            );


    // There may be no forms when every selected
    // registration is RegistrationType === 2.
    const hasRegistrations =
        normalizedRegistrations.length > 0;


    const canShowSubmit =
        hasRegistrations &&
        allFormsReady &&
        (!needsTeam || teamConfirmed);


    // ─────────────────────────────────────────────────────────────────────
    // Render registrations
    // ─────────────────────────────────────────────────────────────────────

    let refIndex = 0;


    function serveRegistration(
        registration,
        quantity
    ) {

        if (
            needsTeam &&
            !teamConfirmed
        ) {
            return null;
        }


        // ================================================================
        // RegistrationType === 0
        // Profile registration
        // ================================================================

        if (
            parseInt(
                registration.RegistrationType
            ) === 0
        ) {

            const globalIndex =
                refIndex;

            const ref =
                formRefs.current[
                    refIndex++
                ];


            return (
                <div
                    key={
                        registration.RegistrationID
                    }
                    className="mb-4"
                >

                    <ProfileSections
                        Event={
                            details.Event
                        }
                        Registration={
                            registration
                        }
                        Team={team}
                    />


                    <ProfileForm
                        ref={ref}
                        globalIndex={
                            globalIndex
                        }
                        onReadyChange={
                            handleFormReadyChange
                        }
                        User={
                            oldUserData.user
                        }
                        Event={
                            details.Event
                        }
                        Registration={
                            registration
                        }
                        AttendeeDates={
                            attendeeDates
                        }
                        Team={team}
                        InsuranceCost={
                            insuranceCost
                        }
                        Index={0}
                    />

                </div>
            );
        }


        // ================================================================
        // RegistrationType === 1
        // Attendee form
        // ================================================================

        if (
            parseInt(
                registration.RegistrationType
            ) === 1
        ) {

            return Array
                .from({
                    length: quantity
                })
                .map((_, i) => {

                    const globalIndex =
                        refIndex;

                    const ref =
                        formRefs.current[
                            refIndex++
                        ];


                    return (
                        <AttendeeForm
                            key={
                                `${registration.RegistrationID}-${i}`
                            }
                            ref={ref}
                            index={i}
                            globalIndex={
                                globalIndex
                            }
                            onReadyChange={
                                handleFormReadyChange
                            }
                            Event={
                                details.Event
                            }
                            Registration={
                                registration
                            }
                            User={
                                oldUserData.user
                            }
                            AttendeeDates={
                                attendeeDates
                            }
                            Quantity={
                                quantity
                            }
                            Team={team}
                            InsuranceCost={
                                insuranceCost
                            }
                        />
                    );
                });
        }


        // ================================================================
        // RegistrationType === 2
        // None / head-count registration
        // ================================================================

        if (
    parseInt(registration.RegistrationType) === 2
) {
    const sectionLabel =
        registration.SectionLabel ||
        registration.RegistrationName ||
        "Attendee";

    const registrationCost =
        parseFloat(
            registration.RegistrationCost || 0
        );

    return (
        <div
            key={registration.RegistrationID}
            className="registrationForms mb-4"
        >
            {Array
                .from({
                    length: quantity
                })
                .map((_, i) => (
                    <div
                        key={`${registration.RegistrationID}-${i}`}
                        className="full-row gray px-4 py-3 mb-3 mt-4 rounded"
                    >
                        <div
                            className="registration-section-label"
                            style={{
                                display: "flex",
                                justifyContent: "space-between",
                                alignItems: "center",
                                width: "100%",
                            }}
                        >
                            <h6>
                                {sectionLabel} {i + 1}
                            </h6>

                            <span>
                                ${registrationCost.toFixed(2)}
                            </span>
                        </div>
                    </div>
                ))}
        </div>
    );
}


        // ================================================================
        // Unknown registration type
        // ================================================================

        return (
            <div
                className="full-row white"
                key={
                    registration.RegistrationID
                }
            >

                <div className="container-fluid">

                    <div className="row mt-4 mb-4">

                        <div className="col-md-12">

                            <div className="alert alert-warning">

                                This registration type has
                                not been fully configured.
                                Please contact the event
                                coordinator or administrator
                                for assistance.

                            </div>

                        </div>

                    </div>

                </div>

            </div>
        );
    }


    // ─────────────────────────────────────────────────────────────────────
    // Page
    // ─────────────────────────────────────────────────────────────────────

    return (

        <div id="registrationWrapper">

            <div className="full-row light-blue">

                <div className="container-fluid px-4 px-md-5">

                    <div className="row text-white py-4">

                        <div className="col-md-12 text-white">

                            <h1 className="text-uppercase mb-1">
                                {details?.Event?.BP_Title}
                            </h1>

                            <p className="mb-0">
                                Please fill out the forms below
                                to register
                            </p>

                        </div>

                    </div>

                </div>

            </div>


            <div className="full-row white">

                <div className="container-fluid px-4 px-md-5 py-4">

                    {/* Team widget */}

                    {serveTeamForm()}


                    {needsTeam &&
                    !teamConfirmed ? (

                        <div className="alert alert-info mt-4">

                            Please create or join a team
                            above before completing
                            your registration.

                        </div>

                    ) : (

                        <>

                            {/* Arrival dates */}

                            {serveArrivalDates()}


                            {/* Registrations */}

                            {normalizedRegistrations.map(
                                ({
                                    Registration,
                                    Quantity
                                }) => (

                                    <div
                                        key={
                                            Registration.RegistrationID
                                        }
                                        className="mb-4"
                                    >

                                        {serveRegistration(
                                            Registration,
                                            Quantity
                                        )}

                                    </div>

                                )
                            )}

                        </>
                    )}


                    {/* Submit */}

                    {canShowSubmit && (

                        <div className="row mt-3 pt-3 border-top">

                            <div className="col-md-12 text-center">

                                <a
                                    className={
                                        `button-default button-green ${
                                            (
                                                !allFormsReady ||
                                                isSubmitting
                                            )
                                                ? "disabled"
                                                : ""
                                        }`
                                    }

                                    onClick={
                                        (
                                            !allFormsReady ||
                                            isSubmitting
                                        )
                                            ? undefined
                                            : handleSubmitAll
                                    }

                                    style={{
                                        display:
                                            "inline-flex",

                                        alignItems:
                                            "center",

                                        justifyContent:
                                            "center",

                                        minHeight:
                                            "44px",

                                        minWidth:
                                            "220px",

                                        opacity:
                                            (
                                                !allFormsReady ||
                                                isSubmitting
                                            )
                                                ? 0.6
                                                : 1,

                                        pointerEvents:
                                            (
                                                !allFormsReady ||
                                                isSubmitting
                                            )
                                                ? "none"
                                                : "auto",

                                        cursor:
                                            (
                                                !allFormsReady ||
                                                isSubmitting
                                            )
                                                ? "not-allowed"
                                                : "pointer",
                                    }}

                                    title={
                                        !allFormsReady
                                            ? "Please fill in all required fields"
                                            : ""
                                    }

                                    role="button"
                                >

                                    {
                                        submissionFailed
                                        ? "Submission stopped — do not retry"
                                        : isSubmitting
                                            ? "Submitting..."
                                                : "Complete Registration"
                                    }

                                </a>

                            </div>

                        </div>

                    )}

                </div>

            </div>

        </div>
    );
};


export default Register;